/**
 * The `useAuth` Zustand store (routing-and-state.md §6), wired to the ADR-0022 two-plane
 * contract: sign in ONCE as an identity (worker-auth), then choose a firm (the picker mints a
 * firm session), then work in that firm (worker-api).
 *
 * Three pieces of state, each from exactly one authoritative source:
 *  - `identity` — the signed-in IDENTITY, from worker-auth (`POST /auth/login`, `GET /auth/me`).
 *    `status` is the identity-level machine: `authenticated` means an identity session exists.
 *  - `user`     — the FIRM principal (per-firm `role` + `user_id`), ONLY from worker-api's
 *    firm-session-scoped `GET /api/v1/auth/me` under the `argus_firm` cookie. It is non-null
 *    only while a firm session is bound. Permissions and route guards read `user.role`, so the
 *    UI can never grant an affordance the data plane (which authorizes against that very
 *    principal) would refuse. The select response's own `role` is a denormalised echo and is
 *    deliberately NOT used.
 *  - `firm`     — the bound firm for DISPLAY (`firm_id` + name from the picker feed). Kept in
 *    per-tab sessionStorage so a reload can still name the firm; it is never a security input
 *    (worker-api binds the firm from the cookie, not from anything the SPA says).
 *
 * Other adaptations of the §6 sketch: the TOTP second step re-POSTs `/auth/login` with
 * `totp_code` (credentials held in memory only between the two steps); `signIn` returns a
 * routing outcome. Switching firm is a fresh select (a brand-new firm session, R1.1) and
 * clears the query cache AND purges the service worker's API caches first — awaited, before the
 * select POST — so no data from the previous firm can render under the next (see `resetSession`).
 *
 * ENG-11: `refresh()` records its in-flight promise so the `_authenticated` guard can AWAIT
 * it instead of racing it (see `resolveAuthReady`).
 *
 * Session loss while the shell is MOUNTED (the "logged out but stuck inside the shell" defect):
 * nothing here navigates — `router/session-guard.ts` subscribes to this store and reacts to a
 * status → `unauthenticated` flip and to the firm principal going null. So the guard can tell a
 * loss the SERVER reported (the heartbeat's `refresh()` found the session gone) from an unbind the
 * SPA started itself (`selectFirm`'s fail-closed unbind, `dropFirm`), those two raise
 * `_firmChangeInFlight` for their duration. `reselectFirm()` is the silent recovery: re-mint the
 * SAME firm's session WITHOUT unbinding first, so the mounted screens can refetch in place. And a
 * `refresh()` that fails INCONCLUSIVELY (offline, a 5xx) keeps whatever is bound: only a refusal
 * (401/403/404) revokes, because the guard now turns a store flip into an immediate bounce.
 */
import { create } from 'zustand';

import {
  api,
  ApiError,
  isApiError,
  isForcePasswordChange,
  isLoginSuccess,
  isTotpRequired,
  type FirmMembership,
  type FirmPrincipal,
  type LoginResponse,
  type Role,
} from '../lib/api.ts';
import { purgeApiRuntimeCaches } from '../lib/pwa/api-runtime-cache.ts';

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

/** The signed-in identity (worker-auth). Identity-scoped: no role, no user_id. */
export interface SignedInIdentity {
  identity_id: string;
  email: string;
  full_name: string;
  totp_enrolled: boolean;
}

/** The FIRM principal (worker-api `GET /api/v1/auth/me`) — the per-firm role + user_id. */
export interface AuthUser {
  user_id: string;
  email: string;
  full_name: string;
  role: Role;
  totp_enrolled?: boolean;
}

/** The bound firm, for display. `legal_name` falls back to the firm_id when unresolved. */
export interface Firm {
  firm_id: string;
  legal_name: string;
  /** Worker-signed logo URL from the principal (every member sees the mark); null = none. */
  logo_url?: string | null;
}

/** What `signIn`/`submitTotp`/`signInWithRecovery` resolve to, so the screen can route. */
export interface SignInResult {
  outcome: 'authenticated' | 'totp_required' | 'force_password_change' | 'totp_enrolment_required';
  /** Preserved from the §6 sketch (`{ totpRequired }`). */
  totpRequired: boolean;
  /** Present when `outcome === 'force_password_change'`. */
  reason?: string;
}

interface AuthState {
  identity: SignedInIdentity | null;
  user: AuthUser | null;
  firm: Firm | null;
  status: AuthStatus;
  signIn: (email: string, password: string) => Promise<SignInResult>;
  submitTotp: (code: string) => Promise<SignInResult>;
  signInWithRecovery: (email: string, password: string, code: string) => Promise<SignInResult>;
  /**
   * Mint a firm-scoped session for one active membership (worker-auth's R1 chokepoint), then
   * load that firm's principal from worker-api. Throws the typed ApiError on a refused select
   * (e.g. a suspended firm → 403) or an unreadable principal; the store is then left with NO
   * bound firm (fail closed).
   */
  selectFirm: (firm: FirmMembership) => Promise<void>;
  /** After a logo upload / clear on the settings screen: refresh the shell mark in place. */
  setFirmLogo: (logoUrl: string | null) => void;
  /** The FIRM session lapsed (worker-api 401) while the identity may be alive: forget the firm
   *  principal and purge its caches, keep the identity, so the picker can re-select in one
   *  click instead of forcing a full sign-in. */
  dropFirm: () => Promise<void>;
  /**
   * Silent recovery after the FIRM session lapsed under a mounted shell: when the identity is
   * still alive and the firm hint names a firm the identity still holds a selectable membership
   * in, mint a fresh firm session for that SAME firm (worker-auth select → worker-api principal)
   * in place — WITHOUT the fail-closed unbind + query-cache clear `selectFirm` performs, so the
   * screens already on screen keep their query observers and can refetch. (The CALLER resets the
   * query cache once this resolves true: a `clear()` under mounted observers orphans them, and
   * nothing would ever refetch.)
   *  - resolves `true`  — re-bound; `user`/`firm` are fresh.
   *  - resolves `false` — identity alive but nothing to return to: no hint, the firm is no longer
   *    a selectable membership, or the mint/principal was refused (the caller goes to the picker).
   *  - rejects          — the identity itself could not be confirmed (`GET /auth/me` failed:
   *    401 = dead → the caller signs out; anything else = inconclusive).
   * `hint` lets a caller that WATCHED the firm disappear pass what it was (`refresh()` drops the
   * stored hint when worker-api refuses the firm). Concurrent calls share one attempt.
   */
  reselectFirm: (hint?: Pick<Firm, 'firm_id'> | null) => Promise<boolean>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
  // ── internal (not part of the §6 public surface) ──
  _pendingCredentials: { email: string; password: string } | null;
  _refreshInFlight: Promise<void> | null;
  _reselectInFlight: Promise<boolean> | null;
  /** True while `selectFirm`/`dropFirm` unbind the firm ON PURPOSE (the session guard ignores it). */
  _firmChangeInFlight: boolean;
}

/**
 * Side effect run on sign-out AND on every firm selection — wired to `queryClient.clear()` in
 * main.tsx, kept behind a registry so the store never imports the QueryClient (no import
 * cycle). Clearing on select is load-bearing: cached queries belong to the previous firm.
 * May be async; it is always AWAITED (see `resetSession`).
 */
let sessionResetSideEffect: () => void | Promise<void> = () => {};
export function registerSessionReset(fn: () => void | Promise<void>): void {
  sessionResetSideEffect = fn;
}

/**
 * Drop everything that belongs to the PREVIOUS session principal, resolving only when it is gone:
 *  1. the registered side effect (the in-memory query cache), and
 *  2. the service worker's API runtime caches in Cache Storage (`purgeApiRuntimeCaches`).
 *
 * (2) is owned HERE, not by the registry, so it cannot be lost to wiring. Cache Storage is keyed
 * by URL only and is shared by every tab of the one origin that now serves every firm; case
 * numbers repeat across firms (ARG-2026-0001 exists in each), so an entry cached under firm A
 * would otherwise be servable for firm B's identical URL without the server ever seeing the
 * request. The purge runs even if the side effect throws; a purge failure propagates.
 */
async function resetSession(): Promise<void> {
  try {
    await sessionResetSideEffect();
  } finally {
    await purgeApiRuntimeCaches();
  }
}

// ── The per-tab firm display hint ────────────────────────────────────────────────
const FIRM_HINT_KEY = 'argus.selected-firm';

function readFirmHint(): Firm | null {
  try {
    const raw = globalThis.sessionStorage?.getItem(FIRM_HINT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Firm>;
    return typeof parsed.firm_id === 'string' && typeof parsed.legal_name === 'string'
      ? { firm_id: parsed.firm_id, legal_name: parsed.legal_name }
      : null;
  } catch {
    return null;
  }
}

function writeFirmHint(firm: Firm | null): void {
  try {
    if (firm) globalThis.sessionStorage?.setItem(FIRM_HINT_KEY, JSON.stringify(firm));
    else globalThis.sessionStorage?.removeItem(FIRM_HINT_KEY);
  } catch {
    /* storage unavailable — the hint is cosmetic */
  }
}

/**
 * Did the server DEFINITIVELY refuse the session — 401 (expired/revoked), 403 (forbidden), 404
 * (worker-api's non-revealing answer to a MISSING `argus_firm` cookie)? A transport failure
 * (status 0: offline, a laptop waking before its network) or a 5xx is INCONCLUSIVE and must not
 * revoke a session that may well be alive.
 */
function isDefinitiveRefusal(err: unknown): boolean {
  return isApiError(err) && (err.status === 401 || err.status === 403 || err.status === 404);
}

/**
 * Mirrors the picker's `isFirmSelectable` (screens/firm-picker-screen.tsx — not imported, the
 * store must not depend on a screen): a suspended/terminated firm's select 403s, so it is not
 * worth a silent attempt. An absent status (the current feed shape) is selectable.
 */
function isSelectableMembership(membership: FirmMembership): boolean {
  return membership.firm_status !== 'suspended' && membership.firm_status !== 'terminated';
}

function principalToUser(p: FirmPrincipal, totpEnrolled: boolean | undefined): AuthUser {
  return {
    user_id: p.user_id,
    email: p.email,
    full_name: p.full_name,
    role: p.role,
    ...(totpEnrolled !== undefined ? { totp_enrolled: totpEnrolled } : {}),
  };
}

export const useAuth = create<AuthState>((set, get) => {
  /** Apply a `/auth/login` response to the store + derive the routing outcome. */
  function applyLoginResponse(
    res: LoginResponse,
    creds: { email: string; password: string },
  ): SignInResult {
    if (isTotpRequired(res)) {
      // Password verified; hold the credentials for the second step (in memory only).
      set({ _pendingCredentials: creds });
      return { outcome: 'totp_required', totpRequired: true };
    }
    if (isForcePasswordChange(res)) {
      set({ _pendingCredentials: null });
      return { outcome: 'force_password_change', totpRequired: false, reason: res.reason };
    }
    if (isLoginSuccess(res)) {
      // A fresh IDENTITY session. worker-auth also expired any argus_firm cookie, so no firm is
      // bound: the next stop is the firm picker.
      writeFirmHint(null);
      set({
        identity: {
          identity_id: res.identity.identity_id,
          email: res.identity.email,
          full_name: res.identity.full_name,
          totp_enrolled: !res.totp_enrolment_required,
        },
        user: null,
        firm: null,
        status: 'authenticated',
        _pendingCredentials: null,
      });
      return {
        outcome: res.totp_enrolment_required ? 'totp_enrolment_required' : 'authenticated',
        totpRequired: false,
      };
    }
    // Unreachable for a response matching the worker-auth contract; fail closed.
    throw new ApiError(502, { code: 'bad_login_response', message: 'Unexpected login response.' });
  }

  /**
   * Mint a firm session for `membership` and load its principal — the shared tail of `selectFirm`
   * and `reselectFirm`. Purges the service worker's API caches AGAIN once the new firm session
   * exists, before its principal loads and anything renders: `queryClient.clear()` does not abort
   * in-flight requests, and a service worker of a PREVIOUS build (still controlling the page until
   * its update is accepted) writes a background revalidation into its cache when the response
   * lands — which, for a request sent under the previous firm's cookie, can be during the select
   * round-trip. Throws the typed ApiError on a refused select / unreadable principal.
   */
  async function bindFirmSession(membership: FirmMembership): Promise<void> {
    const selected = await api.auth.selectFirm(membership.firm_id);
    await purgeApiRuntimeCaches();
    const principal = await api.auth.firmMe();
    const firm: Firm = {
      firm_id: selected.firm_id,
      legal_name: membership.firm_name ?? selected.firm_id,
      logo_url: principal.firm?.logo_url ?? null,
    };
    writeFirmHint(firm);
    set({ user: principalToUser(principal, get().identity?.totp_enrolled), firm });
  }

  return {
    identity: null,
    user: null,
    firm: null,
    status: 'loading',
    _pendingCredentials: null,
    _refreshInFlight: null,
    _reselectInFlight: null,
    _firmChangeInFlight: false,

    signIn: async (email, password) => {
      const res = await api.auth.login({ email, password });
      return applyLoginResponse(res, { email, password });
    },

    submitTotp: async (code) => {
      const creds = get()._pendingCredentials;
      if (!creds) {
        throw new ApiError(400, {
          code: 'no_pending_login',
          message: 'Your sign-in expired. Please start again.',
        });
      }
      const res = await api.auth.login({ ...creds, totp_code: code });
      return applyLoginResponse(res, creds);
    },

    signInWithRecovery: async (email, password, code) => {
      const res = await api.auth.loginWithRecovery({ email, password, recovery_code: code });
      return applyLoginResponse(res, { email, password });
    },

    selectFirm: async (membership) => {
      // Unbind the previous firm FIRST — its principal, its display name, every cached query and
      // every service-worker API cache — so nothing from it can render while (or if) the new mint
      // is in flight or refused. AWAITED: the select POST below is not sent until the previous
      // firm's cached responses are gone from Cache Storage. A purge failure rejects the switch
      // (fail closed) — the store is already left with no bound firm.
      // `_firmChangeInFlight`: this unbind is the SPA's own doing (the sidebar switcher selects in
      // place from a firm route), not a lost session — the session guard must not react to it.
      writeFirmHint(null);
      set({ user: null, firm: null, _firmChangeInFlight: true });
      try {
        await resetSession();
        await bindFirmSession(membership);
      } finally {
        set({ _firmChangeInFlight: false });
      }
    },

    setFirmLogo: (logoUrl) => {
      const firm = get().firm;
      if (firm) set({ firm: { ...firm, logo_url: logoUrl } });
    },

    dropFirm: async () => {
      // A deliberate unbind (the central handler sends the user to the picker itself) — flagged
      // so the session guard does not start a second, redundant recovery.
      writeFirmHint(null);
      set({ user: null, firm: null, _firmChangeInFlight: true });
      try {
        await resetSession();
      } catch {
        /* swallow — the principal is already gone locally */
      } finally {
        set({ _firmChangeInFlight: false });
      }
    },

    reselectFirm: async (hint) => {
      const existing = get()._reselectInFlight;
      if (existing) return existing;
      const run = (async () => {
        try {
          // The identity FIRST: its failure is the caller's sign-out signal and must propagate.
          // (Also the first await — so the latch below is set before anything here can settle.)
          await api.auth.me();
          const target = hint ?? get().firm ?? readFirmHint();
          if (!target) return false;
          // The wanted id, held once so the lookup reads as a plain list search (the R1
          // bind-equality gate lives only in the worker-api tenant-middleware chokepoint).
          const wantedId: string = target.firm_id;
          try {
            const { firms } = await api.auth.firms();
            const membership = firms.find((m) => m.firm_id === wantedId);
            if (!membership || !isSelectableMembership(membership)) return false;
            await bindFirmSession(membership);
            return true;
          } catch {
            // The feed, the mint or the principal was refused: nothing silent is left to try.
            return false;
          }
        } finally {
          set({ _reselectInFlight: null });
        }
      })();
      set({ _reselectInFlight: run });
      return run;
    },

    signOut: async () => {
      // Best-effort server revoke. worker-auth always answers 200 and expires both cookies
      // (fixture: logout — never a 401, even for an expired session); should the request still
      // fail (offline, a proxy error), local state is cleared anyway so the UI cannot present a
      // stale session.
      try {
        await api.auth.logout();
      } catch {
        /* swallow — local sign-out proceeds regardless */
      }
      writeFirmHint(null);
      set({
        identity: null,
        user: null,
        firm: null,
        status: 'unauthenticated',
        _pendingCredentials: null,
      });
      // AWAITED, after the revoke settles (so a response still landing from the old session is
      // purged too): on a shared workstation the next person must not be served this session's
      // cached case data. A purge failure must not block local sign-out; the SPA mints the next
      // firm session only through selectFirm, whose purge runs again and fails closed.
      try {
        await resetSession();
      } catch {
        /* swallow — local sign-out has already completed */
      }
    },

    refresh: async () => {
      // Dedupe concurrent refreshes so the ENG-11 guard awaits the SAME in-flight promise
      // main.tsx kicked off, not a second round-trip.
      const existing = get()._refreshInFlight;
      if (existing) return existing;
      const run = (async () => {
        try {
          const me = await api.auth.me();
          const identity: SignedInIdentity = {
            identity_id: me.identity_id,
            email: me.email,
            full_name: me.full_name,
            totp_enrolled: me.totp_enrolled,
          };
          // Is a firm session bound? Only worker-api can say (it validates the argus_firm
          // cookie). A refusal — no cookie (404), expired (401) — means NO firm; an inconclusive
          // failure (unreachable, a 5xx) keeps a firm that is already bound.
          let user: AuthUser | null = null;
          let firm: Firm | null = null;
          try {
            const principal = await api.auth.firmMe();
            user = principalToUser(principal, identity.totp_enrolled);
            // The principal is authoritative for WHICH firm is bound (worker-api validated the
            // firm cookie); the per-tab hint only fills in when an older worker-api omits it. A
            // fresh tab has no hint, so without this the shell showed no firm name or switcher.
            const known = get().firm ?? readFirmHint();
            firm = principal.firm
              ? {
                  firm_id: principal.firm.firm_id,
                  legal_name: principal.firm.legal_name,
                  logo_url: principal.firm.logo_url ?? null,
                }
              : known
                ? { ...known, logo_url: known.logo_url ?? null }
                : null;
            writeFirmHint(firm);
          } catch (err) {
            if (get().user !== null && !isDefinitiveRefusal(err)) {
              user = get().user;
              firm = get().firm;
            } else {
              writeFirmHint(null);
            }
          }
          set({ identity, user, firm, status: 'authenticated' });
        } catch (err) {
          // Inconclusive while signed in: keep the session (the heartbeat re-checks) — a laptop
          // waking before its network must not be bounced to /login by the session guard. On a
          // cold start (`loading`) there is nothing to keep: resolve unauthenticated, fail closed.
          if (get().status === 'authenticated' && !isDefinitiveRefusal(err)) return;
          writeFirmHint(null);
          set({ identity: null, user: null, firm: null, status: 'unauthenticated' });
        } finally {
          set({ _refreshInFlight: null });
        }
      })();
      set({ _refreshInFlight: run });
      return run;
    },
  };
});

/**
 * ENG-11 — the load-bearing race fix. When status is still `loading`, AWAIT the
 * in-flight `refresh()` (started in main.tsx) before any guard decides to redirect.
 * An authenticated user with a slow refresh is therefore never bounced to /login; an
 * unauthenticated user is redirected only AFTER the refresh resolves negative.
 */
export async function resolveAuthReady(): Promise<void> {
  const s = useAuth.getState();
  if (s.status !== 'loading') return;
  await (s._refreshInFlight ?? s.refresh());
}

/** Test-only: reset the store to its initial state between cases. */
export function __resetAuthStore(): void {
  writeFirmHint(null);
  useAuth.setState({
    identity: null,
    user: null,
    firm: null,
    status: 'loading',
    _pendingCredentials: null,
    _refreshInFlight: null,
    _reselectInFlight: null,
    _firmChangeInFlight: false,
  });
}
